import * as React from 'react';
import {
  Button,
  ButtonPriority,
  ButtonProps,
  ButtonSize,
  PaddingModes,
  TPAComponentsProvider,
} from '..';
import { snap, story, visualize } from 'storybook-snapper';
import { LikeHeart, Share } from '@wix/wix-ui-icons-common/on-stage';
import { buttonDriverFactory } from './Button.driver';
import { classes } from './Button.visual.st.css';
import { VisualAsync } from '../../test/visual/VisualAsync';

function focusStylesSuite() {
  story(`Focus`, () => {
    Object.values(ButtonPriority).forEach((priority) => {
      snap(`${priority}`, (done) => (
        <VisualAsync
          onDone={done}
          action={async (driver) => {
            await driver.click();
          }}
          driverFactory={buttonDriverFactory}
        >
          <Button onFocus={done} priority={priority}>
            {`${priority} focus`}
          </Button>
        </VisualAsync>
      ));
    });
  });
}

function genericTestSuite(name: string, additionalParams?: ButtonProps) {
  story(`${name}`, () => {
    snap(`Size && Priority`, () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.values(ButtonPriority).map((priority) => (
          <div style={{ display: 'flex', gap: 10 }}>
            {Object.values(ButtonSize).map((size) => (
              <div>
                <Button size={size} priority={priority} {...additionalParams}>
                  {`${size}`}
                </Button>
              </div>
            ))}
          </div>
        ))}
      </div>
    ));

    snap(`Mobile: Size && Priority`, () => (
      <TPAComponentsProvider value={{ mobile: true }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.values(ButtonPriority).map((priority) => (
            <div style={{ display: 'flex', gap: 10 }}>
              {Object.values(ButtonSize).map((size) => (
                <div>
                  <Button size={size} priority={priority} {...additionalParams}>
                    {`${size}`}
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </TPAComponentsProvider>
    ));

    snap(`With new icons`, () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.values(ButtonPriority).map((priority) => (
          <div style={{ display: 'flex', gap: 10 }}>
            {Object.values(ButtonSize).map((size) => (
              <div>
                <Button
                  size={size}
                  priority={priority}
                  prefixIcon={<LikeHeart />}
                  suffixIcon={<Share />}
                  {...additionalParams}
                >
                  {`${size}`}
                </Button>
              </div>
            ))}
          </div>
        ))}
      </div>
    ));

    snap(`Disabled`, () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.values(ButtonPriority).map((priority) => (
          <div style={{ display: 'flex', gap: 10 }}>
            {Object.values(ButtonSize).map((size) => (
              <div>
                <Button
                  size={size}
                  priority={priority}
                  disabled
                  {...additionalParams}
                >
                  {`${size}`}
                </Button>
              </div>
            ))}
          </div>
        ))}
      </div>
    ));

    snap(`Wrap Content`, () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.values(ButtonPriority).map((priority) => (
          <div style={{ display: 'flex', gap: 10 }}>
            {Object.values(ButtonSize).map((size) => (
              <div>
                <Button
                  size={size}
                  priority={priority}
                  wrapContent
                  style={{ width: 100 }}
                  {...additionalParams}
                >
                  {`${size} is very long label`}
                </Button>
              </div>
            ))}
          </div>
        ))}
      </div>
    ));
  });
}

function cssVarsTestSuite() {
  story(`Css Variables`, () => {
    snap(`paddings override`, () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <Button
            paddingMode={PaddingModes.explicitPaddings}
            className={classes.allPaddings}
          >
            allPaddings
          </Button>
        </div>
        <div>
          <Button
            paddingMode={PaddingModes.explicitPaddings}
            className={classes.paddingInlineStart}
          >
            paddingInlineStart
          </Button>
        </div>
        <div>
          <Button
            paddingMode={PaddingModes.explicitPaddings}
            className={classes.paddingInlineEnd}
          >
            paddingInlineEnd
          </Button>
        </div>
        <div>
          <Button
            paddingMode={PaddingModes.explicitPaddings}
            className={classes.paddingBlockStart}
          >
            paddingBlockStart
          </Button>
        </div>
        <div>
          <Button
            paddingMode={PaddingModes.explicitPaddings}
            className={classes.paddingBlockEnd}
          >
            paddingBlockEnd
          </Button>
        </div>
      </div>
    ));

    snap('Minimum width', () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[classes.zeroMinimumWidth, classes.customMinimumWidth].map(
          (className) =>
            Object.values(ButtonPriority).map((priority) => (
              <div style={{ display: 'flex', gap: 10 }}>
                {Object.values(PaddingModes).map((paddingMode) => (
                  <div>
                    <Button
                      className={className}
                      paddingMode={paddingMode}
                      priority={priority}
                      style={{ marginLeft: '10px' }}
                      disabled
                    />
                  </div>
                ))}
              </div>
            )),
        )}
      </div>
    ));

    snap('Disabled with border', () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.values(ButtonPriority).map((priority) => (
          <div style={{ display: 'flex', gap: 10 }}>
            {Object.values(PaddingModes).map((paddingMode) => (
              <div>
                <Button
                  className={classes.applyBorder}
                  paddingMode={paddingMode}
                  priority={priority}
                  style={{ marginLeft: '10px' }}
                  disabled
                >
                  {priority}
                </Button>
              </div>
            ))}
          </div>
        ))}
      </div>
    ));

    snap(`Disabled border-color && text-color override`, () =>
      Object.values(ButtonPriority).map((priority) => (
        <Button
          className={classes.disabledColorAndBorder}
          paddingMode={PaddingModes.explicitPaddings}
          priority={priority}
          style={{ marginLeft: '10px' }}
          disabled
        >
          {priority}
        </Button>
      )),
    );

    snap('Disabled border-color && text-color override fallback', () =>
      Object.values(ButtonPriority).map((priority) => (
        <Button
          className={classes.disabledColorAndBorderFallback}
          paddingMode={PaddingModes.explicitPaddings}
          priority={priority}
          style={{ marginLeft: '10px' }}
          disabled
        >
          {priority}
        </Button>
      )),
    );

    snap('Increased spacing between icon and text', () =>
      Object.values(ButtonPriority).map((priority) => (
        <Button
          className={classes.customTextIconSpacing}
          priority={priority}
          prefixIcon={<LikeHeart />}
          suffixIcon={<Share />}
        >
          Press me
        </Button>
      )),
    );
  });
}

const buttonWithStyleOverride = (
  type: 'primary' | 'secondary' | 'basic' | 'basicSecondary',
  style: React.CSSProperties,
  description: string,
  props: any = {},
) => (
  <div style={{ ...style, display: 'column', gap: 2 }}>
    <span>{description}:</span>
    <div style={{ marginTop: '-10px' }}>
      {['tiny', 'small', 'medium', 'large'].map((size) => (
        <span>
          {' '}
          {
            <Button priority={type as ButtonPriority} {...props} size={size}>
              {'legac ' + size.substring(0, 4)}
            </Button>
          }{' '}
          {
            <Button
              priority={type as ButtonPriority}
              {...props}
              paddingMode="dynamicPaddings"
              size={size}
            >
              {' dynam ' + size.substring(0, 4)}
            </Button>
          }{' '}
          <Button
            priority={type as ButtonPriority}
            {...props}
            paddingMode="explicitPaddings"
            size={size}
          >
            {' explic ' + size.substring(0, 4)}
          </Button>
        </span>
      ))}{' '}
    </div>
  </div>
);

const brandThemeByType = (type: 'primary' | 'secondary') => {
  return (
    <div>
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-color-rgb`]: '220,20,20',
        } as React.CSSProperties,
        `--wst-button-${type}-color-rgb`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-border-left-width`]: '4px',
          [`--wst-button-${type}-border-left-style`]: 'solid',
          [`--wst-button-${type}-border-left-color-rgb`]: '220,20,20',
          [`--wst-button-${type}-border-top-width`]: '4px',
          [`--wst-button-${type}-border-top-style`]: 'solid',
          [`--wst-button-${type}-border-top-color-rgb`]: '120,20,20',
          [`--wst-button-${type}-border-right-width`]: '4px',
          [`--wst-button-${type}-border-right-style`]: 'solid',
          [`--wst-button-${type}-border-right-color-rgb`]: '220,20,120',
          [`--wst-button-${type}-border-bottom-width`]: '4px',
          [`--wst-button-${type}-border-bottom-style`]: 'solid',
          [`--wst-button-${type}-border-bottom-color-rgb`]: '220,120,0',
        } as React.CSSProperties,
        `--wst border sides`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wix-ui-tpa-button-main-border-inline-start-width`]: '4px',
          [`--wix-ui-tpa-button-main-border-inline-start-color`]: '220,20,20',
          [`--wix-ui-tpa-button-main-border-block-start-width`]: '4px',
          [`--wix-ui-tpa-button-main-border-block-start-color`]: '120,20,20',
          [`--wix-ui-tpa-button-main-border-inline-end-width`]: '4px',
          [`--wix-ui-tpa-button-main-border-inline-end-color`]: '220,20,120',
          [`--wix-ui-tpa-button-main-border-block-end-width`]: '4px',
          [`--wix-ui-tpa-button-main-border-block-end-style`]: 'solid',
          [`--wix-ui-tpa-button-main-border-block-end-color`]: '220,120,0',
        } as React.CSSProperties,
        `--wix-ui-tpa border sides`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wix-ui-tpa-button-main-border-width`]: '6px',
          [`--wix-ui-tpa-button-main-border-color`]: '255,0,0',
        } as React.CSSProperties,
        `--wix-ui-tpa border width & color`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-border-bottom-color-rgb`]: '220,20,20',
          [`--wix-ui-tpa-button-main-border-width`]: '5px',
        } as React.CSSProperties,
        `--wst button border bottom color`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-border-top-left-radius`]: '12px',
          [`--wst-button-${type}-border-top-right-radius`]: '26px',
          [`--wst-button-${type}-border-bottom-left-radius`]: '26px',
          [`--wst-button-${type}-border-bottom-right-radius`]: '12px',
        } as React.CSSProperties,
        `--wst border sides radius`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wix-ui-tpa-button-main-border-start-start-radius`]: '12px',
          [`--wix-ui-tpa-button-main-border-start-end-radius`]: '26px',
          [`--wix-ui-tpa-button-main-border-end-start-radius`]: '26px',
          [`--wix-ui-tpa-button-main-border-end-end-radius`]: '12px',
        } as React.CSSProperties,
        `--wix-ui-tpa border sides radius`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          '--wix-ui-tpa-button-main-border-radius': '22px',
        } as React.CSSProperties,
        `--wix-ui-tpa border radius`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-text-shadow`]: '8px 8px rgb(220, 120, 120)',
          [`--wst-button-${type}-text-outline`]: '-8px -8px rgb(20, 220, 120)',
          [`--wst-button-${type}-text-highlight`]: 'rgb(220, 220, 120)',
        } as React.CSSProperties,
        `--wst text-shadow && --wst text-outline && --wst text-highlight`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          '--wix-ui-tpa-button-main-text-shadow': '8px 8px rgb(220,120,120)',
          '--wix-ui-tpa-button-main-text-outline': '-8px -8px rgb(20,220,120)',
        } as React.CSSProperties,
        `--wix-ui-tpa text-shadow && --wix-ui-tpa text-outline`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-box-shadow`]: '8px 2px rgb(220, 20, 0)',
        } as React.CSSProperties,
        `--wst box-shadow`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wix-ui-tpa-button-main-box-shadow`]: '8px 2px rgb(220, 20, 0)',
        } as React.CSSProperties,
        `--wix-ui-tpa box-shadow`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          '--wst-system-disabled-color-rgb': '200,220,250',
          [`--wst-${type}-background-color-rgb`]: '30,130,30',
        } as React.CSSProperties,
        `--wst-system-disabled-color-rgb & --wst-${type}-background-color-rgb`,
        { disabled: true },
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-background-color-rgb`]: '0,220,250',
        } as React.CSSProperties,
        `--wst-button-${type}-background-color-rgb`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-text-decoration`]:
            'rgb(0,255,0) wavy underline',
          [`--wst-button-${type}-line-height`]: '25px',
          [`--wst-button-${type}-font-family`]:
            'Courier New, Courier, monospace',
          [`--wst-button-${type}-font-size`]: '20px',
          [`--wst-button-${type}-font-style`]: 'italic',
          [`--wst-button-${type}-font-variant`]: 'small-caps',
          [`--wst-button-${type}-font-weight`]: '700',
        } as React.CSSProperties,
        `--wst-button-${type}-font constituents`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-paragraph-2-text-decoration`]: 'rgb(0,255,0) wavy underline',
          [`--wst-paragraph-2-line-height`]: '25px',
          [`--wst-paragraph-2-font-family`]: 'Courier New, Courier, monospace',
          [`--wst-paragraph-2-font-size`]: '20px',
          [`--wst-paragraph-2-font-style`]: 'italic',
          [`--wst-paragraph-2-font-variant`]: 'small-caps',
          [`--wst-paragraph-2-font-weight`]: '700',
        } as React.CSSProperties,
        `--wst-paragraph-2-font constituents`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wst-button-${type}-letter-spacing`]: '-0.1em',
          [`--wst-button-${type}-text-transform`]: 'uppercase',
        } as React.CSSProperties,
        `--wst-button-${type} letter-spacing & text-transform`,
      )}
      {buttonWithStyleOverride(
        type,
        {
          [`--wix-ui-tpa-button-main-text-font-letter-spacing`]: '-0.1em',
          [`--wix-ui-tpa-button-main-text-font-text-transform`]: 'uppercase',
        } as React.CSSProperties,
        `--wix-ui-tpa-button letter-spacing & text-transform`,
      )}
    </div>
  );
};

const basicGeneric = (type: 'basic' | 'basicSecondary') => (
  <div>
    {buttonWithStyleOverride(
      type,
      {
        [`--wix-ui-tpa-button-main-border-inline-start-width`]: '4px',
        [`--wix-ui-tpa-button-main-border-inline-start-style`]: 'solid',
        [`--wix-ui-tpa-button-main-border-inline-start-color`]: '220,20,20',
        [`--wix-ui-tpa-button-main-border-block-start-width`]: '4px',
        [`--wix-ui-tpa-button-main-border-block-start-color`]: '120,20,20',
        [`--wix-ui-tpa-button-main-border-inline-end-width`]: '4px',
        [`--wix-ui-tpa-button-main-border-inline-end-color`]: '220,20,120',
        [`--wix-ui-tpa-button-main-border-block-end-width`]: '4px',
        [`--wix-ui-tpa-button-main-border-block-end-color`]: '220,120,0',
      } as React.CSSProperties,
      `--wix-ui-tpa border sides`,
    )}
    {buttonWithStyleOverride(
      type,
      {
        [`--wix-ui-tpa-button-main-border-width`]: '6px',
        [`--wix-ui-tpa-button-main-border-color`]: '255,0,0',
      } as React.CSSProperties,
      `--wix-ui-tpa border width & color`,
    )}
    {buttonWithStyleOverride(
      type,
      {
        [`--wix-ui-tpa-button-main-border-start-start-radius`]: '12px',
        [`--wix-ui-tpa-button-main-border-start-end-radius`]: '26px',
        [`--wix-ui-tpa-button-main-border-end-start-radius`]: '26px',
        [`--wix-ui-tpa-button-main-border-end-end-radius`]: '12px',
      } as React.CSSProperties,
      `--wix-ui-tpa border sides radius`,
    )}
    {buttonWithStyleOverride(
      type,
      {
        '--wix-ui-tpa-button-main-border-radius': '22px',
      } as React.CSSProperties,
      `--wix-ui-tpa border radius`,
    )}

    {buttonWithStyleOverride(
      type,
      {
        '--wix-ui-tpa-button-main-text-shadow': '8px 8px rgb(220,120,120)',
        '--wix-ui-tpa-button-main-text-outline': '-8px -8px rgb(20,220,120)',
      } as React.CSSProperties,
      `--wix-ui-tpa text-shadow && --wix-ui-tpa text-outline`,
    )}
    {buttonWithStyleOverride(
      type,
      {
        [`--wix-ui-tpa-button-main-box-shadow`]: '8px 2px rgb(220, 20, 0)',
      } as React.CSSProperties,
      `--wix-ui-tpa box-shadow`,
    )}
    {buttonWithStyleOverride(
      type,
      {
        '--wst-paragraph-2-color-rgb': '220,20,20',
        '--wix-ui-tpa-button-main-border-width': '4px',
      } as React.CSSProperties,
      '--wst-paragraph-2-color-rgb',
    )}
    {buttonWithStyleOverride(
      type,
      {
        '--wst-primary-background-color-rgb': '220,20,20',
      } as React.CSSProperties,
      '--wst-primary-background-color-rgb',
    )}
    {buttonWithStyleOverride(
      type,
      {
        '--wst-system-disabled-color-rgb': '220,20,20',
        '--wst-primary-background-color-rgb': '20,220,20',
      } as React.CSSProperties,
      '--wst-system-disabled-color-rgb & --wst-primary-background-color-rgb',
      { disabled: true },
    )}
    {buttonWithStyleOverride(
      type,
      {
        [`--wst-paragraph-2-text-decoration`]: 'rgb(0,255,0) wavy underline',
        [`--wst-paragraph-2-line-height`]: '25px',
        [`--wst-paragraph-2-font-family`]: 'Courier New, Courier, monospace',
        [`--wst-paragraph-2-font-size`]: '20px',
        [`--wst-paragraph-2-font-style`]: 'italic',
        [`--wst-paragraph-2-font-variant`]: 'small-caps',
        [`--wst-paragraph-2-font-weight`]: '700',
      } as React.CSSProperties,
      `--wst-paragraph-2-${type}-font constituents`,
    )}
    {buttonWithStyleOverride(
      type,
      {
        [`--wix-ui-tpa-button-main-text-font-letter-spacing`]: '-0.1em',
        [`--wix-ui-tpa-button-main-text-font-text-transform`]: 'uppercase',
      } as React.CSSProperties,
      `--wix-ui-tpa-button letter-spacing & text-transform`,
    )}
  </div>
);

const basic = () => basicGeneric('basic');
const basicSecondary = () => basicGeneric('basicSecondary');

function brandThemeVars() {
  story(`Brand Theme Variables`, () => {
    snap(`Primary`, () => brandThemeByType('primary'));
    snap(`Secondary`, () => brandThemeByType('secondary'));
    snap(`Basic`, () => basic());
    snap(`BasicSecondary`, () => basicSecondary());
  });
}

visualize('Button', () => {
  genericTestSuite(PaddingModes.legacy);
  genericTestSuite(PaddingModes.dynamicPaddings, { upgrade: true });
  genericTestSuite(PaddingModes.explicitPaddings, {
    paddingMode: PaddingModes.explicitPaddings,
  });
  focusStylesSuite();
  cssVarsTestSuite();
  brandThemeVars();
});
